import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import AuthGuard from "context/auth/AuthGuard";
import { UserRoles } from "constants/userRoles";
import { useGetUsers } from "hooks/useGetUsers";
import { useGetLessons } from "hooks/useGetLessons";
import { useState } from "react";
import { useConnectUsersWithLessons } from "hooks/useConnectUsersWithLessons";
import { UsersDataTable } from "components/UsersDataTable";
import { LessonsDataTable } from "components/LessonsDataTable";
import ConnectButton from "components/ConnectButton/ConnectButton";
import { MainStack, SubtitleTypography } from './TrainingsManagementPage.styles';

export const TrainingsManagementPage = () => {
    const [usersSelected, setUsersSelected] = useState<number[]>([]);
    const [lessonsSelected, setLessonsSelected] = useState<number[]>([]);
    const [connectedUsersWithLessonsAt, setConnectedUsersWithLessonsAt] = useState<Date | undefined>(undefined);
    const [expandedRows, setExpandedRows] = useState<string[]>([]);
    const { t } = useTranslation();
    const theme = useTheme();
    const { users, fetchingUsers } = useGetUsers();
    const { lessons, fetchingLessons } = useGetLessons();
    const { connectUsersWithLessons, connectingUsersWithLessons } = useConnectUsersWithLessons();

    const handleConnectClick = async () => {
        await connectUsersWithLessons(
            lessonsSelected.map((index) => lessons[index].id),
            usersSelected.map((index) => users[index].id)
        );
        setConnectedUsersWithLessonsAt(new Date());
    }

    return (
        <AuthGuard requiredRoles={[UserRoles.ADMIN, UserRoles.Patron, UserRoles.PatronLight]}>
            <MainStack>
                <Typography variant="h4" fontWeight={'500'}>
                    {t('trainingsManagement.title')}
                </Typography>
                <SubtitleTypography variant="subtitle1">
                    {t('trainingsManagement.subtitle')}
                </SubtitleTypography>
                <Divider />
                <Stack direction={'row'} width={'100%'}>
                    <Box width={'100%'} paddingRight={theme.spacing(5)} paddingTop={theme.spacing(5)}>
                        <UsersDataTable
                            title={t('trainingsManagement.usersListTitle')}
                            users={users}
                            fetchingUsers={fetchingUsers}
                            setParentUsersSelected={setUsersSelected}
                            usersLessonsChangedAt={connectedUsersWithLessonsAt}
                            expandedRows={expandedRows}
                            setExpandedRows={setExpandedRows}
                        />
                    </Box>
                    <ConnectButton
                        onClick={handleConnectClick}
                        disabled={!(lessonsSelected.length > 0 && usersSelected.length > 0)}
                        loading={connectingUsersWithLessons}
                    />
                    <Box width={'100%'} paddingLeft={theme.spacing(5)} paddingTop={theme.spacing(5)}>
                        <LessonsDataTable
                            title={t('trainingsManagement.lessonsListTitle')}
                            lessons={lessons}
                            fetchingLessons={fetchingLessons}
                            setParentLessonsSelected={setLessonsSelected}
                        />
                    </Box>
                </Stack>
            </MainStack>
        </AuthGuard>
    );
}